// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState } from 'react';
import { Link } from '@/i18n/router';
import { useCart } from '@/hooks/useCart';
import { useCartCrossSell } from '@/hooks/useCartCrossSell';
import ProductImage from '@/components/base/ProductImage';

const fmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * CartCrossSell — Versión dinámica (Fase 2 auditoría hardcodes)
 *
 * FUENTE DE DATOS:
 * - Primaria: product_relation_cards_view WHERE relation_type = 'frequently_bought_together'
 *   cruzado con los product_id del carrito actual.
 * - Variantes: product_variant_catalog_active (primera variante activa por sort_order).
 * - Fallback: productos con mejor rating_avg de product_relation_cards_view
 *   cuando no hay FBT para los productos del carrito.
 *
 * DEGRADACIÓN SIN variantId:
 * - Si un producto no tiene variante activa disponible, se muestra un CTA
 *   "Ver producto" (Link a la ficha) en lugar del botón "Añadir".
 *   Nunca se intenta un addToCart sin variantId.
 */
const CartCrossSell = () => {
  useStoreLanguage();
  const { items, addToCart } = useCart();
  const [addingId, setAddingId] = useState<number | null>(null);

  // IDs de productos ya en el carrito (solo productos, no bundles)
  const cartProductIds = items
    .filter((item) => item.itemType === 'product')
    .map((item) => item.productId);

  const { suggestions, loading } = useCartCrossSell({
    cartProductIds,
    limit: 3,
  });

  // No renderizar si no hay sugerencias o está cargando sin datos previos
  if (!loading && suggestions.length === 0) return null;
  if (loading && suggestions.length === 0) return null;

  const handleAdd = async (productId: number, variantId: string, name: string, price: number, image: string, category: string) => {
    if (addingId !== null) return;
    setAddingId(productId);
    try {
      await addToCart(
        {
          id: productId,
          name,
          price: price.toFixed(2).replace('.', ','),
          image,
          category,
          variantId,
        },
        1,
      );
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="px-0 py-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-sparkling-line text-amber-500 text-base"></i>
          </div>
          <span className="font-inter text-sm font-semibold text-gray-700">{tr("Combina bien con")}</span>
        </div>
        <span className="font-inter text-[11px] text-gray-400">{tr("Añade y ahorra en envío")}</span>
      </div>

      <div className="space-y-2.5">
        {tr(suggestions.map((product) => {
          const isCurrentlyAdding = addingId === product.productId;
          const hasVariant = product.variantId !== null && product.variantStock > 0;
          const isOutOfStock = product.variantId !== null && product.variantStock <= 0;
          const slug = product.slug || String(product.productId);

          return (
            <div
              key={product.productId}
              className="flex items-center gap-3 bg-gray-50 rounded-lg p-2.5 border border-gray-100 hover:border-forest-200 transition-colors"
            >
              {/* Imagen */}
              <Link
                to={`/product/${slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-white block"
                tabIndex={-1}
              >
                <ProductImage
                  src={product.image}
                  alt={tr(product.name)}
                  productName={product.name}
                  className="w-full h-full object-cover object-top"
                  fallbackClassName="w-full h-full"
                  lazy={false}
                />
              </Link>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/product/${slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="block"
                >
                  <p className="font-inter text-sm font-medium text-gray-800 truncate leading-tight hover:text-forest-700 transition-colors">
                    {tr(product.name)}
                  </p>
                </Link>
                <p className="font-inter text-xs text-gray-500 mt-0.5">
                  {tr(product.variantLabel && `${product.variantLabel} · `)}
                  {tr(fmt.format(product.price))} €
                  {tr(product.isOnSale && product.originalPrice && product.originalPrice > product.price && (
                    <span className="ml-1 line-through text-gray-400">
                      {tr(fmt.format(product.originalPrice))} €
                    </span>
                  ))}
                </p>
              </div>

              {/* CTA */}
              {tr(hasVariant ? (
                <button
                  onClick={() =>
                    handleAdd(
                      product.productId,
                      product.variantId!,
                      product.name,
                      product.price,
                      product.image,
                      '',
                    )
                  }
                  disabled={isCurrentlyAdding}
                  className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    isCurrentlyAdding
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-forest-600 text-white hover:bg-forest-700 active:scale-95'
                  }`}
                >
                  {tr(isCurrentlyAdding ? (
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-add-line text-xs"></i>
                      </div>{tr("Añadir")}</>
                  ))}
                </button>
              ) : isOutOfStock ? (
                /* Variante existe pero sin stock */
                <Link
                  to={`/product/${slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </div>{tr("Ver")}</Link>
              ) : (
                /* Sin variante disponible — degradación elegante */
                <Link
                  to={`/product/${slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>{tr("Ver")}</Link>
              ))}
            </div>
          );
        }))}
      </div>
    </div>
  );
};

export default CartCrossSell;
